'use client'

import React from 'react'
import Image from 'next/image'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function AboutContent() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-center">About mixus</h1>
      
      <div className="mb-10">
        <Card className="overflow-hidden">
          <div className="relative h-64 sm:h-80 w-full">
            <Image 
              src="/images/about-hero.jpg" 
              alt="mixus team collaboration"
              fill
              className="object-cover"
              priority
            />
          </div>
          <CardContent className="p-6">
            <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
            <p className="text-lg mb-4">
              At mixus, we're building the first collaborative AI platform for the workplace, enabling real-time collaboration between AI and teams.
            </p>
            <p className="text-lg">
              We believe that the future of work is a harmonious blend of human intelligence and artificial intelligence, working together to solve complex problems and drive innovation.
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-3">What We Do</h2>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Enable seamless collaboration between teams and AI</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Provide access to leading AI models in a collaborative environment</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Connect people with the right expertise at the right time</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Integrate with your existing tools and workflows</span>
              </li>
            </ul>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-3">Our Values</h2>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Human-centered AI that augments rather than replaces</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Privacy and security by design</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Transparency in how we build and operate</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>Continuous innovation and improvement</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
      
      <Card className={cn("mb-10")}>
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Our Story</h2>
          <p className="mb-4">
            Founded in 2023, mixus was born from a simple observation: while AI tools were becoming increasingly powerful, they were largely being used in isolation, missing the collaborative aspect that makes human teams so effective.
          </p>
          <p className="mb-4">
            Our founders, with backgrounds in AI, collaboration software, and enterprise solutions, set out to create a platform that would bridge this gap – bringing together the computational power of AI with the creativity, expertise, and judgment of human teams.
          </p>
          <p>
            Today, mixus is helping teams across industries work more effectively by facilitating seamless collaboration between people and AI, unlocking new levels of productivity and innovation.
          </p>
        </CardContent>
      </Card>
      
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4">Join Us on Our Journey</h2>
        <p className="text-lg mb-6">
          We're just getting started, and we'd love for you to be part of our story.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a 
            href="mailto:support@mixus.ai" 
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Contact Us
          </a>
          <a 
            href="https://www.mixus.ai/careers" 
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 text-base font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Careers
          </a>
        </div>
      </div>
    </div>
  )
} 